import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ArrowRight, BookOpen, Calendar, ShieldCheck, ChevronDown } from 'lucide-react';
// --- Theme Constants ---
const COLORS = {
  teal: '#1a5f5f',
  tealDark: '#0d4f4f',
  cream: '#faf8f5',
  white: '#ffffff',
  text: '#2d2d2d',
  textLight: '#525252',
  stone: '#e5e7eb'
};
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/wi1tvtYQf4Ko1qYVgyXx55/WhatsApp_Image_2026-02-04_at_5.48.45_PM.jpg';
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
// --- Reusable Components ---
const Section = ({
  children,
  className = '',
  bg = 'cream'
}: {
  children: React.ReactNode;
  className?: string;
  bg?: 'cream' | 'white';
}) => {
  return <section className={`py-16 md:py-24 px-6 md:px-8 ${bg === 'white' ? 'bg-white' : 'bg-[#faf8f5]'} ${className}`} data-id="element-2">
      <div className="max-w-3xl mx-auto" data-id="element-3">{children}</div>
    </section>;
};
const Button = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => <button onClick={onClick} className={`
      bg-[#1a5f5f] hover:bg-[#0d4f4f] text-[#faf8f5] 
      font-medium text-lg px-8 py-4 rounded-md 
      transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5
      flex items-center justify-center gap-2 w-full md:w-auto
      ${className}
    `} data-id="element-4">
    {children}
    <ArrowRight className="w-5 h-5" data-id="element-5" />
  </button>;
const Divider = () => <div className="flex justify-center py-8 opacity-20" data-id="element-6">
    <div className="w-16 h-px bg-[#1a5f5f]" data-id="element-7"></div>
  </div>;
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-8">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-10" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-id="element-11">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-12">
              <h2 className="text-xl font-serif font-bold text-[#1a5f5f]" data-id="element-13">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-14">
                <X className="w-5 h-5 text-[#525252]" data-id="element-15" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] text-[#525252] text-sm leading-relaxed space-y-4" data-id="element-16">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
};
// --- Legal Content ---
const PrivacyContent = () => <>
    <p data-id="element-17">
      <strong data-id="element-18">Última actualización:</strong> Febrero 2026
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-19">
      1. Información que Recopilamos
    </h3>
    <p data-id="element-20">
      Recopilamos información que nos proporcionas directamente, como tu nombre
      y dirección de correo electrónico cuando realizas una compra.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-21">
      2. Uso de la Información
    </h3>
    <p data-id="element-22">Utilizamos la información recopilada para:</p>
    <ul className="list-disc pl-6 space-y-2" data-id="element-23">
      <li data-id="element-24">Procesar y entregar tu compra</li>
      <li data-id="element-25">Enviarte actualizaciones relacionadas con tu producto</li>
      <li data-id="element-26">Responder a tus consultas y solicitudes</li>
    </ul>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-27">
      3. Protección de Datos
    </h3>
    <p data-id="element-28">
      Implementamos medidas de seguridad técnicas y organizativas para proteger
      tu información personal contra acceso no autorizado, alteración o
      destrucción.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-29">
      4. Compartir Información
    </h3>
    <p data-id="element-30">
      No vendemos, comercializamos ni transferimos tu información personal a
      terceros, excepto para procesar pagos de forma segura.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-31">5. Tus Derechos</h3>
    <p data-id="element-32">
      Tienes derecho a acceder, corregir o eliminar tu información personal.
      Para ejercer estos derechos, contáctanos directamente.
    </p>
  </>;
const TermsContent = () => <>
    <p data-id="element-33">
      <strong data-id="element-34">Última actualización:</strong> Febrero 2026
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-35">
      1. Aceptación de Términos
    </h3>
    <p data-id="element-36">
      Al comprar y acceder a "Renacer Espiritual Sin Pánico", aceptas estos
      términos y condiciones en su totalidad.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-37">
      2. Naturaleza del Producto
    </h3>
    <p data-id="element-38">
      Este es un producto digital educativo y de bienestar general.{' '}
      <strong data-id="element-39">No constituye consejo médico, psicológico ni terapéutico.</strong>{' '}
      No reemplaza la atención profesional de salud mental.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-40">
      3. Licencia de Uso
    </h3>
    <p data-id="element-41">
      Al comprar, recibes una licencia personal e intransferible para usar el
      material. No está permitido:
    </p>
    <ul className="list-disc pl-6 space-y-2" data-id="element-42">
      <li data-id="element-43">Redistribuir o revender el contenido</li>
      <li data-id="element-44">Compartir los archivos con terceros</li>
      <li data-id="element-45">Usar el material con fines comerciales</li>
    </ul>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-46">
      4. Entrega Digital
    </h3>
    <p data-id="element-47">
      El producto se entrega digitalmente a tu correo electrónico inmediatamente
      después de la compra. Debido a la naturaleza digital del producto, no se
      aceptan devoluciones una vez entregado.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-48">
      5. Limitación de Responsabilidad
    </h3>
    <p data-id="element-49">
      Los resultados pueden variar según cada persona. No garantizamos
      resultados específicos. El uso del material es bajo tu propia
      responsabilidad.
    </p>

    <h3 className="text-lg font-bold text-[#2d2d2d] mt-6" data-id="element-50">
      6. Propiedad Intelectual
    </h3>
    <p data-id="element-51">
      Todo el contenido está protegido por derechos de autor. Queda prohibida la
      reproducción total o parcial sin autorización expresa.
    </p>
  </>;
// --- Main Page Component ---
export function SalesPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToOffer = () => {
    const offerSection = document.getElementById('offer-section');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen w-full overflow-x-hidden selection:bg-[#1a5f5f] selection:text-white" data-id="element-52">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-20 md:pt-32 md:pb-24 px-6 bg-[#faf8f5]" data-id="element-53">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-54">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-55">
            <motion.div variants={fadeInUp} data-id="element-56">
              <span className="inline-block px-3 py-1 text-xs font-bold tracking-widest text-[#1a5f5f] uppercase border border-[#1a5f5f]/20 rounded-full mb-6" data-id="element-57">
                Protocolo NS-21
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a5f5f] leading-[1.1] mb-4" data-id="element-58">
                RENACER ESPIRITUAL SIN PÁNICO
              </h1>
              <p className="text-xl md:text-2xl text-[#525252] font-serif italic" data-id="element-59">
                Claridad en 14 días, sin apagar tu espiritualidad.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-4" data-id="element-60">
              <Button onClick={scrollToOffer} data-id="element-61">Descargar ahora</Button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative" data-id="element-62">
            <div className="absolute inset-0 bg-[#1a5f5f]/5 rounded-2xl transform rotate-3 scale-105 z-0" data-id="element-63"></div>
            <img src={PRODUCT_IMAGE} alt="Renacer Espiritual Sin Pánico Book and Journal" className="relative z-10 rounded-lg shadow-xl w-full object-cover aspect-[4/3]" data-id="element-64" />
          </motion.div>
        </div>
      </section>

      {/* 2. IDENTIFICATION (The Scene) */}
      <Section bg="white" data-id="element-65">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="prose prose-lg prose-stone mx-auto" data-id="element-66">
          <h2 className="text-3xl md:text-4xl text-[#1a5f5f] font-bold mb-8 text-center" data-id="element-67">
            Si últimamente sientes que tu mente se fue "demasiado lejos", no
            estás solo/a.
          </h2>

          <p className="text-[#525252] leading-relaxed mb-6" data-id="element-68">
            Sigues con tu vida, pero algo cambió: te cuesta concentrarte, te
            cuesta decidir, te cuesta "estar".
          </p>

          <p className="text-[#525252] leading-relaxed mb-6" data-id="element-69">
            No porque no quieras. Sino porque dentro de ti hay un ruido
            constante: interpretaciones, preguntas, "¿y si…?", ideas enormes que
            no entran en un día normal.
          </p>

          <p className="text-[#525252] leading-relaxed font-medium border-l-4 border-[#1a5f5f] pl-6 py-2 bg-[#faf8f5]" data-id="element-70">
            Y lo peor es esto: sientes que si no lo entiendes, no puedes
            avanzar. Como si la claridad fuera una puerta… y tu mente se hubiera
            quedado trabada en el marco.
          </p>
        </motion.div>
      </Section>

      {/* 3. THE LOGICAL ERROR */}
      <Section bg="cream" data-id="element-71">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center max-w-2xl mx-auto" data-id="element-72">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#1a5f5f] mb-6" data-id="element-73">
            El error lógico (que empeora todo)
          </h3>
          <p className="text-lg text-[#525252] mb-8" data-id="element-74">
            Cuando aparece esta confusión, la mayoría hace lo lógico:
          </p>

          <div className="bg-white p-8 rounded-lg shadow-sm border border-[#1a5f5f]/10 mb-8" data-id="element-75">
            <h4 className="text-xl font-bold text-[#2d2d2d] mb-2" data-id="element-76">
              Buscar más contenido.
            </h4>
            <p className="text-[#525252]" data-id="element-77">
              Más explicaciones. Más mapas. Más teorías.
            </p>
          </div>

          <p className="text-lg text-[#525252]" data-id="element-78">
            Y en espiritualidad eso es infinito. Entonces tu mente abre
            pestañas, abre posibilidades, abre miedo… y te deja con menos vida.
          </p>
        </motion.div>
      </Section>

      {/* 4. WHAT'S REALLY HAPPENING */}
      <Section bg="white" data-id="element-79">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-80">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#1a5f5f] mb-8 text-center" data-id="element-81">
            Qué está pasando en realidad
          </h3>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-82">
            <div className="space-y-6" data-id="element-83">
              <p className="text-lg text-[#525252]" data-id="element-84">No es que "te falta fe".</p>
              <p className="text-lg text-[#525252]" data-id="element-85">
                No es que "no eres fuerte".
              </p>
              <p className="text-lg text-[#525252]" data-id="element-86">
                Y tampoco es que "tienes que entregarte y aguantar".
              </p>
            </div>

            <div className="bg-[#faf8f5] p-8 rounded-xl border border-[#1a5f5f]/10" data-id="element-87">
              <p className="font-serif text-xl text-[#1a5f5f] mb-4" data-id="element-88">
                Muchas veces es esto:
              </p>
              <ul className="space-y-4" data-id="element-89">
                <li className="flex items-start gap-3" data-id="element-90">
                  <div className="w-2 h-2 rounded-full bg-[#1a5f5f] mt-2.5" data-id="element-91"></div>
                  <span className="text-[#525252]" data-id="element-92">
                    Tu <strong data-id="element-93">sistema nervioso</strong> está más sensible
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-94">
                  <div className="w-2 h-2 rounded-full bg-[#1a5f5f] mt-2.5" data-id="element-95"></div>
                  <span className="text-[#525252]" data-id="element-96">
                    Tu mente intenta <strong data-id="element-97">controlar</strong> esa sensibilidad
                    con significado
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-98">
                  <div className="w-2 h-2 rounded-full bg-[#1a5f5f] mt-2.5" data-id="element-99"></div>
                  <span className="text-[#525252]" data-id="element-100">
                    Ese significado no tiene fin
                  </span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-[#1a5f5f]/10" data-id="element-101">
                <p className="font-bold text-[#2d2d2d]" data-id="element-102">
                  Resultado: confusión crónica + desconexión del día a día.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* 5. THE SOLUTION (NS-21) */}
      <Section bg="cream" data-id="element-103">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-12" data-id="element-104">
          <span className="text-sm font-bold tracking-widest text-[#1a5f5f] uppercase mb-2 block" data-id="element-105">
            Mecanismo Único
          </span>
          <h3 className="text-3xl md:text-4xl font-serif font-bold text-[#2d2d2d] mb-6" data-id="element-106">
            La solución: Protocolo NS-21
          </h3>
          <p className="text-lg text-[#525252] max-w-2xl mx-auto" data-id="element-107">
            <strong data-id="element-108">Renacer Espiritual Sin Pánico</strong> no intenta explicarte
            "todo lo espiritual". Te devuelve <strong data-id="element-109">suelo</strong>.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-110">
          {[{
          step: '1',
          title: 'NERVIO',
          subtitle: '(primero)',
          desc: 'Bajas la alarma del cuerpo.'
        }, {
          step: '2',
          title: 'SIGNIFICADO',
          subtitle: '(después)',
          desc: 'Le pones bordes a la mente para que deje de irse al infinito.'
        }, {
          step: '3',
          title: 'INTEGRACIÓN',
          subtitle: '(recién ahí)',
          desc: 'Eliges prácticas espirituales con "tierra", sin intensidad.'
        }].map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.2
        }} className="bg-white p-8 rounded-lg shadow-sm border-t-4 border-[#1a5f5f] text-center" data-id="element-111">
              <div className="w-12 h-12 bg-[#faf8f5] rounded-full flex items-center justify-center text-[#1a5f5f] font-bold text-xl mx-auto mb-4" data-id="element-112">
                {item.step}
              </div>
              <h4 className="text-xl font-bold text-[#2d2d2d] mb-1" data-id="element-113">
                {item.title}
              </h4>
              <span className="text-sm text-[#525252] italic mb-4 block" data-id="element-114">
                {item.subtitle}
              </span>
              <p className="text-[#525252]" data-id="element-115">{item.desc}</p>
            </motion.div>)}
        </div>

        <div className="text-center mt-12" data-id="element-116">
          <p className="text-lg font-serif italic text-[#1a5f5f]" data-id="element-117">
            No es teoría. Es un sistema de estabilidad.
          </p>
        </div>
      </Section>

      {/* 6. DELIVERABLES */}
      <Section bg="white" data-id="element-118">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-119">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="order-2 md:order-1" data-id="element-120">
            <h3 className="text-3xl font-serif font-bold text-[#2d2d2d] mb-8" data-id="element-121">
              Qué recibes
            </h3>

            <div className="space-y-6" data-id="element-122">
              <div className="flex gap-4" data-id="element-123">
                <div className="bg-[#faf8f5] p-3 rounded-lg h-fit" data-id="element-124">
                  <BookOpen className="w-6 h-6 text-[#1a5f5f]" data-id="element-125" />
                </div>
                <div data-id="element-126">
                  <h4 className="text-xl font-bold text-[#2d2d2d]" data-id="element-127">
                    Ebook PDF
                  </h4>
                  <p className="text-[#525252]" data-id="element-128">
                    Guía completa, sobria y práctica.
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-129">
                <div className="bg-[#faf8f5] p-3 rounded-lg h-fit" data-id="element-130">
                  <Calendar className="w-6 h-6 text-[#1a5f5f]" data-id="element-131" />
                </div>
                <div data-id="element-132">
                  <h4 className="text-xl font-bold text-[#2d2d2d]" data-id="element-133">
                    Diario NS-21 imprimible
                  </h4>
                  <p className="text-[#525252]" data-id="element-134">
                    21 días: 1 página por día para volver a ti.
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-135">
                <div className="bg-[#faf8f5] p-3 rounded-lg h-fit" data-id="element-136">
                  <ShieldCheck className="w-6 h-6 text-[#1a5f5f]" data-id="element-137" />
                </div>
                <div data-id="element-138">
                  <h4 className="text-xl font-bold text-[#2d2d2d] mb-2" data-id="element-139">
                    Protocolos cortos para:
                  </h4>
                  <ul className="space-y-2 text-[#525252] text-sm md:text-base" data-id="element-140">
                    <li className="flex items-center gap-2" data-id="element-141">
                      <Check className="w-4 h-4 text-[#1a5f5f]" data-id="element-142" /> Cortar
                      espirales mentales en minutos
                    </li>
                    <li className="flex items-center gap-2" data-id="element-143">
                      <Check className="w-4 h-4 text-[#1a5f5f]" data-id="element-144" /> Reducir la
                      sobrecarga de información
                    </li>
                    <li className="flex items-center gap-2" data-id="element-145">
                      <Check className="w-4 h-4 text-[#1a5f5f]" data-id="element-146" /> Recuperar
                      decisiones simples sin drama
                    </li>
                    <li className="flex items-center gap-2" data-id="element-147">
                      <Check className="w-4 h-4 text-[#1a5f5f]" data-id="element-148" /> Sostener
                      claridad sin "endurecerte"
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="order-1 md:order-2 bg-[#faf8f5] p-8 rounded-2xl flex items-center justify-center" data-id="element-149">
            <img src={PRODUCT_IMAGE} alt="Preview of the materials" className="rounded-lg shadow-lg w-full transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-150" />
          </motion.div>
        </div>
      </Section>

      {/* 7. FOR WHOM / NOT FOR WHOM */}
      <Section bg="cream" data-id="element-151">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-152">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-xl shadow-sm border-t-4 border-[#1a5f5f]" data-id="element-153">
            <h3 className="text-xl font-bold text-[#2d2d2d] mb-6 flex items-center gap-2" data-id="element-154">
              <Check className="w-6 h-6 text-[#1a5f5f]" data-id="element-155" /> Esto es para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-156">
              {['Te abriste a lo espiritual y terminaste con la mente sobrecargada', 'Sientes que todo significa "demasiado"', 'Quieres volver a tu rutina con calma sin renunciar a tu búsqueda'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#525252]" data-id="element-157">
                  <Check className="w-5 h-5 text-[#1a5f5f] shrink-0 mt-0.5" data-id="element-158" />
                  <span data-id="element-159">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-xl shadow-sm border-t-4 border-gray-300" data-id="element-160">
            <h3 className="text-xl font-bold text-[#525252] mb-6 flex items-center gap-2" data-id="element-161">
              <X className="w-6 h-6 text-gray-400" data-id="element-162" /> Esto NO es para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-163">
              {['Quieres promesas mágicas o "la verdad final"', 'Buscas reemplazar terapia o atención médica (este material no es eso)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#525252]" data-id="element-164">
                  <X className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" data-id="element-165" />
                  <span data-id="element-166">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* 8. WHAT CHANGES IN 14 DAYS */}
      <Section bg="white" data-id="element-167">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center max-w-2xl mx-auto" data-id="element-168">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#2d2d2d] mb-8" data-id="element-169">
            Lo que suele cambiar en 14 días
          </h3>
          <p className="text-sm uppercase tracking-widest text-[#525252] mb-6" data-id="element-170">
            (Sin promesas raras)
          </p>

          <p className="text-lg text-[#525252] mb-8" data-id="element-171">
            Lo más común no es "algo místico". Es algo mucho más útil:
          </p>

          <div className="text-3xl md:text-4xl font-serif font-bold text-[#1a5f5f] mb-8" data-id="element-172">
            Menos ruido y más capacidad de estar presente.
          </div>

          <p className="text-lg text-[#525252] mb-4" data-id="element-173">
            No porque "te iluminaste". Sino porque recuperaste suelo.
          </p>
          <p className="text-lg font-medium text-[#2d2d2d]" data-id="element-174">
            Y cuando hay suelo, aparece la claridad.
          </p>
        </motion.div>
      </Section>

      {/* 9. FINAL OFFER + CTA */}
      <section id="offer-section" className="py-20 px-6 bg-[#1a5f5f] text-[#faf8f5] text-center" data-id="element-175">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto" data-id="element-176">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4" data-id="element-177">
            RENACER ESPIRITUAL SIN PÁNICO
          </h2>
          <p className="text-xl md:text-2xl opacity-90 mb-8 font-light" data-id="element-178">
            (NS-21)
          </p>

          <div className="bg-[#faf8f5]/10 backdrop-blur-sm p-8 rounded-xl inline-block w-full max-w-md mb-8 border border-[#faf8f5]/20" data-id="element-179">
            <p className="text-lg mb-2" data-id="element-180">
              Ebook PDF + Diario imprimible 21 días
            </p>
            <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-181">
              <span className="text-4xl font-bold" data-id="element-182">$8.950 ARS</span>
            </div>
            <p className="text-sm opacity-80 mb-6" data-id="element-183">Precio de validación</p>
            <a href="https://renacerespiritual.shop/cart/46883779313815:1" target="_blank" rel="noopener noreferrer" className="w-full bg-[#faf8f5] text-[#1a5f5f] hover:bg-white font-bold text-lg px-8 py-4 rounded-md transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-184">
              Descargar ahora <ArrowRight className="w-5 h-5" data-id="element-185" />
            </a>
          </div>

          <p className="text-sm opacity-60 max-w-md mx-auto" data-id="element-186">
            Pago seguro. Acceso inmediato a tu correo electrónico.
          </p>
        </motion.div>
      </section>

      {/* 10. FAQ MINI */}
      <Section bg="white" data-id="element-187">
        <div className="max-w-2xl mx-auto" data-id="element-188">
          <h3 className="text-2xl font-serif font-bold text-[#2d2d2d] mb-8 text-center" data-id="element-189">
            Preguntas Frecuentes
          </h3>

          <div className="space-y-6" data-id="element-190">
            {[{
            q: '¿Esto reemplaza terapia o medicina?',
            a: 'No. Es educativo y de bienestar general.'
          }, {
            q: '¿Tengo que dejar lo espiritual?',
            a: 'No. El enfoque es espiritualidad con tierra: primero estabilidad.'
          }, {
            q: '¿Cuánto tiempo por día?',
            a: 'Versión mínima: 10–15 minutos.'
          }].map((item, i) => <div key={i} className="border-b border-gray-100 pb-6 last:border-0" data-id="element-191">
                <h4 className="font-bold text-[#1a5f5f] mb-2 text-lg" data-id="element-192">
                  {item.q}
                </h4>
                <p className="text-[#525252]" data-id="element-193">{item.a}</p>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-[#faf8f5] py-12 text-center text-[#525252] text-sm border-t border-[#1a5f5f]/10" data-id="element-194">
        <div className="flex justify-center gap-6 mb-4" data-id="element-195">
          <button onClick={() => setActiveModal('privacy')} className="hover:text-[#1a5f5f] transition-colors underline underline-offset-2" data-id="element-196">
            Política de Privacidad
          </button>
          <span className="text-[#1a5f5f]/30" data-id="element-197">|</span>
          <button onClick={() => setActiveModal('terms')} className="hover:text-[#1a5f5f] transition-colors underline underline-offset-2" data-id="element-198">
            Términos y Condiciones
          </button>
        </div>
        <p data-id="element-199">
          © {new Date().getFullYear()} Renacer Espiritual. Todos los derechos
          reservados.
        </p>
      </footer>

      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-200">
        <PrivacyContent data-id="element-201" />
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-202">
        <TermsContent data-id="element-203" />
      </Modal>
    </div>;
}